/*
 *  -----------------------------------------------------------------------
 *    <copyright file="manage-observations.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useLazyQuery, useQuery } from '@apollo/client/react';

import { getDashboardPanels } from '@components/central-monitoring/manage-observation/manage-observation.helper';
import { ObsRowAction } from '@components/central-monitoring/observations-grid-v2.interface';
import AddAction from '../../components/central-monitoring/add-action';
import NavigationService from '@components-clean/page-nav/navigation.service';
import { Permissions } from 'contexts/user-context';
import { useAuthorization, useBoolean, useLocalState, useTranslation } from 'hooks';
import { GET_CM_OBSERVATIONS_AND_ACTIONS, GET_DATASET_REVIEWS, GET_DATASETS } from 'queries/central-monitoring';
import { GET_SITES, GET_SITES_DELETE } from 'queries/sites';
import { GET_RESTRICTED_STUDY } from 'queries/studies';
import Constants from 'utils/constants';
import NotificationService from 'utils/notification';
import ManageObservation from 'views/components/central-monitoring/manage-observation/manage-observation';
import ObservationsGridV2 from 'views/components/central-monitoring/observations-grid-v2';
import Loading from 'views/components/core/loading';
import PageTitle from 'views/components/core/page-title/page-title';
import { SelectWithSearchDataArrayInterface } from 'views/components/core/select-with-categories/select-with-categories.interface';
import Error from 'views/components/error/error';
import { getDatasetDates, getPanelName } from '@utils/cm-observations.service';
import { AllRoutes } from '@utils/router/all-routes';
import { dataSets, dataSetReviews } from '@queries/generated/central-monitoring.generated';
import {
    StudyFields_Study_members_StudyMember,
    StudyFields,
    CentralMonitoringObservationAndActionFields,
    CentralMonitoringActionFields,
    SiteFields,
    DataSetFields,
    PanelForObservationFields,
} from '@queries/generated/fragments.generated';

/*
Use queries/central-monitoring.ts:

GET_CM_OBSERVATIONS(studyId: ObjectId)
Gets all of the CM observations (and associated actions) for the study

SAVE_CM_OBSERVATION(observation: CentralMonitoringObservationInput)
Saves a CM observation

SAVE_CM_ACTION(observationId: ObjectId, action: CentralMonitoringObservationActionInput)
Saves a CM action
 */

const ManageCMObservations = ({
    study,
    handleDeleteObs,
    handleRestoreObs,
}: {
    study: StudyFields;
    handleDeleteObs: Function;
    handleRestoreObs: Function;
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { state } = useLocalState();
    const user = useAuthorization();

    const [loadData, { loading, error, data }] = useLazyQuery(GET_CM_OBSERVATIONS_AND_ACTIONS);
    const [loadStudyData, { loading: loadingStudy, data: studyData }] = useLazyQuery(GET_RESTRICTED_STUDY);
    const { data: dataSites } = useQuery(GET_SITES, {
        variables: {
            studyId: study?.id ?? '',
        },
    });

    const { data: dataSetsData } = useQuery<dataSets>(GET_DATASETS, {
        variables: {
            studyId: study?.id,
        },
    });

    const { data: dataSetsReviewsData } = useQuery<dataSetReviews>(GET_DATASET_REVIEWS, {
        variables: {
            studyId: study?.id,
        },
    });

    const [datasetDates, setDatasetDates] = useState<DataSetFields[]>(null);

    useEffect(() => {
        const datasetDatesResult = getDatasetDates(dataSetsData, dataSetsReviewsData, user.systemAdminLevel);

        setDatasetDates(datasetDatesResult);
    }, [dataSetsData, dataSetsReviewsData]);

    const canView = user?.canRead({
        module: Constants.Modules.CM,
        element: Constants.DataElements.CentralMonitoring,
        permission: Permissions.Read,
    });
    const canEdit = user?.canWrite({
        module: Constants.Modules.CM,
        element: Constants.DataElements.CentralMonitoring,
        permission: Permissions.ReadWrite,
    });

    const [observations, setObservations] = useState([]);
    const [currentObservation, setCurrentObservation] = useState(null);
    const [panels, setPanels] = useState<PanelForObservationFields[]>([]);
    const [OwnerOptions, setOwnerOptions] = useState([] as SelectWithSearchDataArrayInterface[]);

    const addActionOpen = useBoolean(false);
    const editObservationOpen = useBoolean(false);

    const { data: siteDeletedData } = useQuery(GET_SITES_DELETE, {
        variables: {
            studyId: study?.id,
        },
    });

    useEffect(() => {
        const config = state.scoringData?.config ?? state.studyData?.config;
        if (config) {
            setPanels(getDashboardPanels(config.dashboards, config.panels));
        }
    }, [state.scoringData, state.studyData]);

    useEffect(() => {
        if (study) {
            loadData({ variables: { studyId: study.id } });
            loadStudyData({ variables: { id: study.id } });
        }
    }, [loadData, study]);

    useEffect(() => {
        if (studyData?.study) {
            setOwnerOptions(
                studyData.study.members.map((member: StudyFields_Study_members_StudyMember) => ({
                    id: member.id,
                    text: `${member.firstName} ${member.lastName}`,
                    active: member.active,
                })),
            );
        }
    }, [studyData]);

    useEffect(() => {
        if (data?.centralMonitoringObservations?.length) {
            // eslint-disable-next-line max-len
            const array = data.centralMonitoringObservations.map(
                (observation: CentralMonitoringObservationAndActionFields) => {
                    const _activeActions = observation.actions?.filter(
                        (action: CentralMonitoringActionFields) => !action.deleted,
                    );
                    // eslint-disable-next-line max-len
                    const _recentActionDate = _activeActions
                        .map((action: CentralMonitoringActionFields) => action.requestDate)
                        .sort()
                        .reverse()[0];
                    const numberOfOpenActions = _activeActions.filter(
                        (action: CentralMonitoringActionFields) => !action.completedDate,
                    );
                    const owner: SelectWithSearchDataArrayInterface = observation.ownerId
                        ? OwnerOptions.find((el: SelectWithSearchDataArrayInterface) => el.id === observation.ownerId)
                        : null;
                    let obsSite = { ...dataSites?.sites?.find((item: SiteFields) => item.id === observation.siteId) };
                    if (!obsSite) {
                        obsSite = siteDeletedData?.sitesDelete?.find(
                            (item: SiteFields) => item.id === observation.siteId,
                        );
                        if (obsSite) {
                            obsSite.Deleted = true;
                        }
                    }
                    return {
                        ...observation,
                        confirmedCause: observation.confirmedCause ?? '',
                        comments: observation.comments ?? '',
                        potentialCause: observation.potentialCause ?? '',
                        _recentActionDate: _recentActionDate ? new Date(_recentActionDate) : null,
                        _openAction: numberOfOpenActions?.length,
                        _numberOfActions: _activeActions?.length,
                        _owner: owner,
                        _ownerLabel: owner?.text ?? '',
                        _datasetDate: observation.dataSet?.dataCutDate ?? null,
                        _panelName: getPanelName(observation.panel, observation.type),
                        _site: obsSite?.name ?? '',
                        // fields needed for View Site Dashboard btn
                        _siteStatus: obsSite?.status ?? '',
                        _siteId: obsSite?.id ?? '',
                        _siteDeleted: obsSite?.Deleted ?? false,
                        // end
                        _isClosedDateReadOnly:
                            observation.actions.findIndex(
                                (el: CentralMonitoringActionFields) => !el.completedDate && !el.deleted,
                            ) >= 0,
                        ...(observation.deleted && observation.audit
                            ? {
                                  _deleteReason: observation.audit.reason,
                                  _deleteDate: observation.audit.created,
                                  _deletedBy: `${observation.audit.userFirstName} ${observation.audit.userLastName}`,
                              }
                            : {}),
                        actions: observation.actions.map((action: CentralMonitoringActionFields) => {
                            // eslint-disable-next-line no-shadow
                            const owner: SelectWithSearchDataArrayInterface = action.ownerId
                                ? OwnerOptions.find(
                                      (el: SelectWithSearchDataArrayInterface) => el.id === action.ownerId,
                                  )
                                : null;
                            return {
                                ...action,
                                observationId: observation.id,
                                _obsDeleted: observation.deleted,
                                _site: obsSite?.name ?? '',
                                // fields needed for View Site Dashboard btn
                                _siteStatus: obsSite?.status ?? '',
                                _siteId: obsSite?.id ?? '',
                                _siteDeleted: obsSite?.Deleted ?? false,
                                // end
                                _owner: owner,
                                ownerLabel: owner?.text ?? '',
                                _isCompletedDateReadOnly: Boolean(observation.closedDate),
                                ...(action.deleted && action.audit
                                    ? {
                                          _deleteReason: action.audit.reason,
                                          _deleteDate: action.audit.created,
                                          _deletedBy: `${action.audit.userFirstName} ${action.audit.userLastName}`,
                                      }
                                    : {}),
                            };
                        }),
                    };
                },
            );
            setObservations(array);
        }
    }, [data, dataSites, OwnerOptions]);

    const rowActions: ObsRowAction = (item) => {
        if (item.deleted) {
            return [
                {
                    label: 'cm:restoreObservation',
                    icon: ['fal', 'undo'],
                    onClick: () => handleRestoreObs(item),
                    enabled: canEdit,
                },
            ];
        }

        return [
            {
                label: 'ra:editObservation',
                icon: ['fal', 'edit'],
                onClick: () => {
                    setCurrentObservation(item);
                    editObservationOpen.setTrue();
                },
                enabled: canEdit,
            },
            {
                label: 'ra:assignAction',
                icon: ['fal', 'link'],
                onClick: () => {
                    setCurrentObservation(item);
                    addActionOpen.setTrue();
                },
                enabled: canEdit && !item.closedDate,
            },
            {
                label: 'cm:deleteObservation',
                icon: ['fal', 'trash'],
                onClick: () => handleDeleteObs(item),
                enabled: canEdit,
            },
            {
                label: 'cm:viewSiteDashboard',
                icon: ['far', 'eye'],
                onClick: () => {
                    NavigationService.setViewLegacySiteDashboard();
                    navigate(AllRoutes.path('cm-site-dashboard') as string, {
                        state: {
                            skipSelectDate: true,
                            showDashboard: true,
                            siteId: item._siteId as string | null | undefined,
                        },
                    });
                },
                enabled:
                    item._siteDeleted === false && item._siteStatus?.length
                        ? (studyData.study.sitesStatusesToExclude?.indexOf(item._siteStatus) < 0 ?? true)
                        : false,
            },
        ];
    };

    if (!study) return <h1>{t('noStudySelected')}</h1>;

    if (error) return <Error error={error} />;

    if (loading || !data || loadingStudy) return <Loading float />;

    if (!canView) return <h1>{t('unauthorised')}</h1>;

    return (
        <>
            <AddAction
                study={study}
                observationId={currentObservation?.id}
                owners={OwnerOptions}
                open={addActionOpen.value}
                canEdit
                onClose={(action: string | null) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else if (action === 'assign') {
                        // eslint-disable-next-line max-len
                        NotificationService.showNotification(
                            t('ra:successfulCreateActionAssignedToObservation', { id: currentObservation?.recordId }),
                        );
                    }
                    setCurrentObservation(null);
                    addActionOpen.setFalse();
                }}
            />
            <ManageObservation
                study={study}
                owners={OwnerOptions}
                currentObservation={currentObservation}
                type={currentObservation?.type}
                open={editObservationOpen.value}
                sites={dataSites?.sites ?? []}
                canEdit={canEdit}
                panels={panels}
                panelId={currentObservation?.panelId}
                onClose={(action: string | null) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else {
                        if (action === 'edit')
                            // eslint-disable-next-line max-len
                            NotificationService.showNotification(
                                `${t('ra:observation')} ${currentObservation.recordId} ${t('ra:successfulUpdated')}`,
                            );
                        setCurrentObservation(null);
                        editObservationOpen.setFalse();
                    }
                }}
                datasetDates={datasetDates}
            />
            <PageTitle text={t('ra:manageObservations')} />
            <ObservationsGridV2
                title={t('ra:observationsAndActions')}
                study={studyData?.study}
                observations={observations}
                canEdit={canEdit}
                hideCheckbox
                rowActions={rowActions}
            />
        </>
    );
};

export default ManageCMObservations;
